import { AssignmentOutlined } from '@mui/icons-material'
import { Box, keyframes, Stack, Typography } from '@mui/material'

import { differenceInSeconds, parseISO } from 'date-fns'
import type React from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useInterval } from 'react-use'

// Shown in place of the menu while the selected timeslot is still before its
// orders-open time, replacing the previous static "this timeslot is not open
// yet" message. Adapted from the design mockup — badge, label, live time and a
// shimmering track — but built from MUI primitives and tenant theme tokens
// rather than the mockup's example hex, so each brand picks up its own
// primary/secondary automatically.

const SHIMMER_WIDTH_PCT = 50

const shimmerMove = keyframes`
  from { left: -${SHIMMER_WIDTH_PCT}%; }
  to { left: 100%; }
`

// Each tick re-enters from a dimmed state, so the countdown reads as a beat
// rather than a jumping number.
const tickFade = keyframes`
  from { opacity: 0.4; }
  to { opacity: 1; }
`

const SECONDS_IN_MINUTE = 60
const SECONDS_IN_HOUR = 60 * SECONDS_IN_MINUTE
const SECONDS_IN_DAY = 24 * SECONDS_IN_HOUR
const DAYS_IN_WEEK = 7

const pad = (value: number) => String(value).padStart(2, '0')

/** Localised unit suffixes, from `cc:navBar.cutOffAbbreviations`. */
export type DurationUnits = { weeks: string; days: string; hours: string }

/**
 * Coarse at the top of the range, precise at the bottom: a wait measured in
 * weeks gains nothing from a ticking seconds digit, but the final hour does.
 *
 * Units are injected rather than read from i18n here, so the function stays
 * pure — the suffixes differ per language (Dutch hours are "u", Italian days
 * "g"), so they cannot be hard-coded.
 */
export const formatTimeUntilOpen = (totalSeconds: number, units: DurationUnits): string => {
  const remaining = Number.isFinite(totalSeconds) ? Math.max(totalSeconds, 0) : 0
  const days = Math.floor(remaining / SECONDS_IN_DAY)
  const hours = Math.floor((remaining % SECONDS_IN_DAY) / SECONDS_IN_HOUR)
  const minutes = Math.floor((remaining % SECONDS_IN_HOUR) / SECONDS_IN_MINUTE)
  const seconds = remaining % SECONDS_IN_MINUTE

  if (days >= DAYS_IN_WEEK) {
    return `${Math.floor(days / DAYS_IN_WEEK)}${units.weeks} ${days % DAYS_IN_WEEK}${units.days}`
  }
  if (days >= 1) {
    return `${days}${units.days} ${hours}${units.hours}`
  }
  if (hours >= 1) {
    return `${hours}:${pad(minutes)}:${pad(seconds)}`
  }
  return `${pad(minutes)}:${pad(seconds)}`
}

const MASK = 'linear-gradient(to right, transparent, black 30%, black 70%, transparent)'

type Props = {
  /** ISO timestamp at which ordering opens for the selected timeslot. */
  ordersOpenTime: string
}

const OrderingOpensCountdown: React.FC<Props> = ({ ordersOpenTime }) => {
  const { t } = useTranslation()
  const [now, setNow] = useState(() => new Date())

  useInterval(() => setNow(new Date()), 1000)

  // Derived rather than stored, so switching timeslot re-renders the new
  // remaining time immediately instead of waiting for the next tick.
  const timeUntilOpen = formatTimeUntilOpen(differenceInSeconds(parseISO(ordersOpenTime), now), {
    weeks: t('cc:navBar.cutOffAbbreviations.weeks'),
    days: t('cc:navBar.cutOffAbbreviations.days'),
    hours: t('cc:navBar.cutOffAbbreviations.hours'),
  })

  return (
    <Stack
      alignItems="center"
      spacing={3}
      sx={{
        width: '100%',
        maxWidth: 420,
        marginX: 'auto',
        marginY: 4,
        paddingX: 4,
        paddingY: 5,
        borderRadius: 3,
        bgcolor: 'background.canvas',
      }}>
      <Box
        sx={{
          width: 36,
          height: 36,
          flexShrink: 0,
          borderRadius: '50%',
          bgcolor: 'primary.main',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <AssignmentOutlined sx={{ fontSize: 18, color: 'primary.contrastText' }} />
      </Box>

      <Stack alignItems="center" spacing={0.75}>
        <Typography
          variant="overline"
          sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary' }}>
          {t('cc:orderingOpensIn')}
        </Typography>
        <Typography
          // Remounting on each new value restarts the fade.
          key={timeUntilOpen}
          role="timer"
          sx={{
            fontSize: 46,
            fontWeight: 500,
            lineHeight: 1,
            letterSpacing: '-0.02em',
            fontVariantNumeric: 'tabular-nums',
            '@media (prefers-reduced-motion: no-preference)': {
              animation: `${tickFade} 0.15s ease`,
            },
          }}>
          {timeUntilOpen}
        </Typography>
      </Stack>

      {/* Decorative pacing bar — indeterminate, it does not track progress
          towards the open time. */}
      <Box
        aria-hidden
        sx={{
          position: 'relative',
          width: '100%',
          height: 8,
          borderRadius: 1,
          bgcolor: 'divider',
          overflow: 'hidden',
        }}>
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            width: `${SHIMMER_WIDTH_PCT}%`,
            borderRadius: 1,
            bgcolor: 'secondary.main',
            WebkitMaskImage: MASK,
            maskImage: MASK,
            '@media (prefers-reduced-motion: no-preference)': {
              animation: `${shimmerMove} 2s cubic-bezier(0.4, 0, 0.2, 1) infinite`,
            },
            // Reduced-motion: drop the sweep entirely rather than parking a
            // half-width block in the track.
            '@media (prefers-reduced-motion: reduce)': { display: 'none' },
          }}
        />
      </Box>
    </Stack>
  )
}

export default OrderingOpensCountdown
